import React, { EffectCallback, useEffect, useRef, MutableRefObject } from 'react'
import EditorJS from '@editorjs/editorjs'
import Paragraph from '@editorjs/paragraph'
import Table from '@editorjs/table'
import Header from '@editorjs/header'
import List from '@editorjs/list'
import BreakLine from 'editorjs-break-line'
import { EditorJsWrapper } from 'Pages/admin/agreements/common/components/styled'
import { reckSanitize } from 'Helpers'

interface ComponentProps {
	initialData: string
	isEdit?: boolean
	updateEditor?: (data: string) => void
	isRevertEditor?: boolean
	replaceFields?: Record<string, string>
	isReady?: MutableRefObject<boolean>
}

export const EditorJsComponent = ({
	isEdit = false,
	updateEditor,
	initialData,
	isRevertEditor,
	replaceFields,
	isReady,
}: ComponentProps): JSX.Element => {
	const EDITOR_HOLDER_ID = 'editorjs'

	const editorInstance = useRef<EditorJS>(null)

	const dataWithFields = (data: string): string => {
		let ans = data
		Object.keys(replaceFields).forEach(
			// eslint-disable-next-line no-return-assign
			(key): void =>
				// @ts-ignore
				(ans = ans.replaceAll(`@${key}@`, replaceFields?.[key] ?? ''))
		)
		return ans
	}

	const initEditor = (): void => {
		const editor = new EditorJS({
			holder: EDITOR_HOLDER_ID,
			readOnly: true,
			data: reckSanitize(JSON.parse(replaceFields ? dataWithFields(initialData) : initialData)),
			onReady: (): void => {
				editorInstance.current = editor
				if (isReady?.current) isReady.current = true
			},
			onChange: async (): Promise<void> => {
				const content = await editorInstance.current.saver.save()
				updateEditor(JSON.stringify(content))
			},
			autofocus: true,
			tools: {
				header: {
					class: Header,
					inlineToolbar: true,
					config: {
						placeholder: 'Enter a header',
						levels: [2, 3, 4],
						defaultLevel: 3,
					},
				},
				paragraph: {
					class: Paragraph,
					inlineToolbar: true,
				},
				list: {
					class: List,
					inlineToolbar: true,
				},
				table: {
					class: Table,
					inlineToolbar: true,
					config: {
						rows: 2,
						cols: 3,
					},
				},
				breakLine: {
					class: BreakLine,
					inlineToolbar: true,
					shortcut: 'CMD+SHIFT+ENTER',
				},
			},
		})
	}

	const destroyEditor = (): void => {
		if (editorInstance.current) editorInstance.current.destroy()
		editorInstance.current = null
	}

	useEffect((): EffectCallback => {
		if (Object.keys(initialData)?.length) {
			initEditor()
		}

		return (): void => {
			destroyEditor()
		}
	}, [initialData])

	useEffect((): void => {
		if (editorInstance.current) editorInstance.current.readOnly.toggle()
	}, [isEdit])

	useEffect((): void => {
		if (Object.keys(initialData)?.length && isRevertEditor) {
			destroyEditor()
			initEditor()
		}
	}, [isRevertEditor])

	return (
		<>
			<EditorJsWrapper id={EDITOR_HOLDER_ID} />
		</>
	)
}
